/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { useEffect, useRef, useState } from 'react';
import { useNextRouter } from '@/data/Content/_NextRouter';

interface UseHeaderVisibilityOptions {
	showThreshold?: number;
	hideDelta?: number;
	showDelta?: number;
	maxValidDelta?: number;
	toggleCooldownMs?: number;
}

export const useHeaderVisibility = ({
	showThreshold = 100,
	hideDelta = 10,
	showDelta = 30,
	maxValidDelta = 200,
	toggleCooldownMs = 320,
}: UseHeaderVisibilityOptions = {}) => {
	const [visible, setVisible] = useState(true);
	const lastScrollYRef = useRef(0);
	const accumulatedRef = useRef(0);
	const lastToggleAtRef = useRef(0);
	const visibleRef = useRef(true);
	const tickingRef = useRef(false);
	const router = useNextRouter();

	useEffect(() => {
		const reset = () => {
			visibleRef.current = true;
			accumulatedRef.current = 0;
			lastToggleAtRef.current = 0;
			lastScrollYRef.current = 0;
			setVisible(true);
		};
		router.events.on('routeChangeComplete', reset);
		return () => {
			router.events.off('routeChangeComplete', reset);
		};
	}, [router.events]);

	useEffect(() => {
		const update = () => {
			tickingRef.current = false;
			const currentScrollY = Math.max(window.scrollY, 0);
			const rawDelta = currentScrollY - lastScrollYRef.current;
			lastScrollYRef.current = currentScrollY;

			if (Math.abs(rawDelta) > maxValidDelta) return;
			if (rawDelta === 0) return;

			if (Math.sign(rawDelta) !== Math.sign(accumulatedRef.current)) {
				accumulatedRef.current = 0;
			}
			accumulatedRef.current += rawDelta;

			const now = Date.now();
			if (now - lastToggleAtRef.current < toggleCooldownMs) return;

			if (currentScrollY <= showThreshold) {
				if (!visibleRef.current) {
					visibleRef.current = true;
					lastToggleAtRef.current = now;
					accumulatedRef.current = 0;
					setVisible(true);
				}
				return;
			}

			if (
				visibleRef.current &&
				accumulatedRef.current > hideDelta &&
				currentScrollY > showThreshold
			) {
				visibleRef.current = false;
				lastToggleAtRef.current = now;
				accumulatedRef.current = 0;
				setVisible(false);
			} else if (!visibleRef.current && accumulatedRef.current < -showDelta) {
				visibleRef.current = true;
				lastToggleAtRef.current = now;
				accumulatedRef.current = 0;
				setVisible(true);
			}
		};

		const handleScroll = () => {
			if (!tickingRef.current) {
				tickingRef.current = true;
				window.requestAnimationFrame(update);
			}
		};

		lastScrollYRef.current = Math.max(window.scrollY, 0);
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [showThreshold, hideDelta, showDelta, maxValidDelta, toggleCooldownMs]);

	return visible;
};
